import React, { useState } from "react";
import ImageUploading from "react-images-uploading";
import { Pencil, Trash2 } from "lucide-react";
import config from "../../config";

const ProjectForm = () => {
  const [formData, setFormData] = useState({
    title_fr: "",
    title_en: "",
    description_fr: "",
    description_en: "",
    category: "",
  });

  const [images, setImages] = useState([]);
  const maxImages = 5;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (images.length === 0) {
      alert("Veuillez ajouter au moins une image (principale).");
      return;
    }

    const data = new FormData();
    data.append("title_fr", formData.title_fr);
    data.append("title_en", formData.title_en);
    data.append("description_fr", formData.description_fr);
    data.append("description_en", formData.description_en);
    data.append("category", formData.category);

    const convertDataUrlToFile = async (dataUrl, filename) => {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      return new File([blob], filename, { type: blob.type });
    };

    const mainFile = await convertDataUrlToFile(images[0].data_url, "main.jpg");
    data.append("mainImage", mainFile);

    for (let i = 1; i < images.length; i++) {
      const file = await convertDataUrlToFile(
        images[i].data_url,
        `image-${i}.jpg`
      );
      data.append("additionalImages", file);
    }

    try {
      const res = await fetch(`${config.API_BASE_URL}/api/projects`, {
        method: "POST",
        body: data,
      });

      if (!res.ok) throw new Error("Erreur lors de l'envoi");

      alert("Projet envoyé avec succès !");
      setFormData({
        title_fr: "",
        title_en: "",
        description_fr: "",
        description_en: "",
        category: "",
      });
      setImages([]);
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de l'envoi.");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-light rounded-lg p-8 shadow-lg w-full max-w-3xl mx-auto flex flex-col space-y-6 text-black"
    >
      {/* Champs en grille */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="title_fr" className="block font-semibold mb-1">
            Titre (FR)
          </label>
          <input
            id="title_fr"
            name="title_fr"
            type="text"
            value={formData.title_fr}
            onChange={handleChange}
            placeholder="Titre en français"
            className="p-3 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>

        <div>
          <label htmlFor="title_en" className="block font-semibold mb-1">
            Titre (EN)
          </label>
          <input
            id="title_en"
            name="title_en"
            type="text"
            value={formData.title_en}
            onChange={handleChange}
            placeholder="Title in English"
            className="p-3 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>

        <div>
          <label htmlFor="description_fr" className="block font-semibold mb-1">
            Description (FR)
          </label>
          <textarea
            id="description_fr"
            name="description_fr"
            value={formData.description_fr}
            onChange={handleChange}
            placeholder="Description en français"
            className="p-3 border rounded-lg resize-none h-28 w-full focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>

        <div>
          <label htmlFor="description_en" className="block font-semibold mb-1">
            Description (EN)
          </label>
          <textarea
            id="description_en"
            name="description_en"
            value={formData.description_en}
            onChange={handleChange}
            placeholder="Description in English"
            className="p-3 border rounded-lg resize-none h-28 w-full focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
      </div>

      {/* Catégorie */}
      <div>
        <label htmlFor="category" className="block font-semibold mb-1">
          Catégorie
        </label>
        <select
          id="category"
          name="category"
          value={formData.category}
          onChange={handleChange}
          className="p-3 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-accent"
          required
        >
          <option value="">-- Sélectionner une catégorie --</option>
          <option value="Artificial Intelligence">
            Artificial Intelligence
          </option>
          <option value="Industrial Design">Industrial Design</option>
          <option value="Graphic Design">Graphic Design</option>
          <option value="Creative Lab">Creative Lab</option>
        </select>
      </div>

      {/* Images */}
      <div className="space-y-2">
        <label className="block font-semibold text-gray-700">
          Images du projet
        </label>
        <ImageUploading
          multiple
          value={images}
          onChange={setImages}
          maxNumber={maxImages}
          dataURLKey="data_url"
        >
          {({
            imageList,
            onImageUpload,
            onImageRemoveAll,
            onImageUpdate,
            onImageRemove,
          }) => (
            <div className="flex flex-col items-start space-y-4">
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={onImageUpload}
                  className="bg-accent text-white px-4 py-2 rounded-lg hover:bg-deepblue transition"
                >
                  Ajouter des images
                </button>
                {imageList.length > 0 && (
                  <button
                    type="button"
                    onClick={onImageRemoveAll}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
                  >
                    Supprimer toutes
                  </button>
                )}
              </div>

              <div className="flex flex-wrap gap-4 mt-2">
                {imageList.map((image, index) => (
                  <div
                    key={index}
                    className="relative group w-32 h-32 rounded-lg overflow-hidden border shadow-sm"
                  >
                    <img
                      src={image.data_url}
                      alt={
                        index === 0
                          ? "Aperçu principal du projet"
                          : `Aperçu secondaire ${index}`
                      }
                      className="object-cover w-full h-full"
                    />

                    <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 flex items-center justify-center space-x-3 transition">
                      <button
                        type="button"
                        onClick={() => onImageUpdate(index)}
                        title="Modifier"
                        className="text-white hover:text-accent p-1"
                      >
                        <Pencil size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onImageRemove(index)}
                        title="Supprimer"
                        className="text-white hover:text-red-500 p-1"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                    <span className="absolute bottom-0 left-0 bg-white text-xs text-gray-700 px-1 rounded-tr">
                      {index === 0 ? "Principale" : "Secondaire"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ImageUploading>
      </div>

      {/* Bouton submit */}
      <div className="flex justify-center mt-4">
        <button
          type="submit"
          className="w-full bg-accent text-white py-3 px-4 rounded-lg font-semibold hover:bg-deepblue transition"
        >
          Enregistrer le projet
        </button>
      </div>
    </form>
  );
};

export default ProjectForm;
